import { useState, useEffect, useCallback } from 'react';
import { Plus, Save, AlertCircle, CheckCircle, Keyboard, ArrowLeft, Package } from 'lucide-react';
import { HeaderSection, OrderSection } from './sections';
import { KeyboardShortcuts } from './KeyboardShortcuts';
import { ASNFormData } from '../types/asn';
import { validateASN, getErrorsAsRecord } from '../utils/validation';
import { exportToJSON, downloadJSON } from '../utils/export';
import { useKeyboardShortcut } from '../utils/keyboard';
import { fetchDocumentById, updateDocument } from '../utils/documents';
import type { TradingPartnerWithTemplate } from '../types/tradingPartner';
import { applyTemplateToFormData, createEmptyOrder, createEmptyLineItem } from '../utils/templateApplicator';
import { overlayCustomerDefaults, getCachedCustomerDefaults } from '../utils/customerDefaults';
import { eventBus } from '../utils/eventBus';
import { generateReferenceNumber } from '../utils/generators';
import { isSaveBlockedByExpiry, EXPIRED_SAVE_MSG } from './sessionSaveGuard';

const initialFormData: ASNFormData = {
  purposeCode: '00',
  shipmentIdentification: '',
  date: '',
  time: '',
  timeCode: 'CT',
  dates: [],
  grossWeight: '',
  packagingCode: 'PCS',
  ladingQuantity: '',
  carrierIdentificationQualifier: '2',
  carrierIdentificationCode: '',
  carrierReferenceNumber: '',
  transportationMethod: 'LT',
  routing: '',
  equipmentDescriptionCode: 'TL',
  equipmentInitial: '',
  equipmentNumber: '',
  shipmentMethodOfPayment: 'CA',
  scacCode: '',
  references: [],
  orders: [],
};

interface ASNFormProps {
  documentId?: string;
  tradingPartner?: TradingPartnerWithTemplate;
  externalData?: Partial<ASNFormData>;
  /** When true the form was opened via Mode B (full document re-open). Shows "Edit ASN (Copy)" in the title. */
  isCopy?: boolean;
  onBack: () => void;
  sessionExpired?: boolean;
}

export function ASNForm({ documentId, tradingPartner, externalData, isCopy = false, onBack, sessionExpired = false }: ASNFormProps) {
  const [formData, setFormData] = useState<ASNFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showValidation, setShowValidation] = useState(false);
  const [jsonPreview, setJsonPreview] = useState('');
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveErrorDetail, setSaveErrorDetail] = useState<string | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [savedFormSnapshot, setSavedFormSnapshot] = useState<string>(JSON.stringify(initialFormData));

  useEffect(() => {
    if (documentId) {
      loadDocument();
    } else {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');
      const timeStr = today.toTimeString().substring(0, 5).replace(':', '');

      let newFormData: ASNFormData = {
        ...initialFormData,
        date: dateStr,
        time: timeStr,
      };

      if (tradingPartner?.template?.template_config) {
        newFormData = applyTemplateToFormData(
          newFormData,
          overlayCustomerDefaults(tradingPartner.template.template_config, getCachedCustomerDefaults(), 'asn', tradingPartner.id),
        );
      }

      if (externalData) {
        newFormData = { ...newFormData, ...externalData };
      }

      setFormData(newFormData);
      setSavedFormSnapshot(JSON.stringify(newFormData));
    }
  }, [documentId, tradingPartner, externalData]);

  const loadDocument = async () => {
    if (!documentId) return;

    setLoading(true);
    try {
      const doc = await fetchDocumentById(documentId);
      if (doc) {
        setFormData(doc.data);
        setSavedFormSnapshot(JSON.stringify(doc.data));
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Failed to load document:', error);
      alert('Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const currentSnapshot = JSON.stringify(formData);
    setHasUnsavedChanges(currentSnapshot !== savedFormSnapshot);
  }, [formData, savedFormSnapshot]);

  const handleHeaderChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddOrder = useCallback(() => {
    const templateConfig = tradingPartner?.template?.template_config;
    const newOrder = createEmptyOrder(templateConfig, formData.orders.length, formData.date);
    setFormData((prev) => ({
      ...prev,
      orders: [...prev.orders, newOrder],
    }));
  }, [formData.date, formData.orders.length, tradingPartner]);

  const handleRemoveOrder = (orderIndex: number) => {
    setFormData((prev) => ({
      ...prev,
      orders: prev.orders.filter((_, i) => i !== orderIndex),
    }));
  };

  const handleOrderChange = (orderIndex: number, field: string, value: any) => {
    setFormData((prev) => {
      const newOrders = [...prev.orders];
      newOrders[orderIndex] = { ...newOrders[orderIndex], [field]: value };
      return { ...prev, orders: newOrders };
    });

    const errorKey = `order_${orderIndex}_${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleAddLineItem = (orderIndex: number) => {
    const currentOrder = formData.orders[orderIndex];
    const templateConfig = tradingPartner?.template?.template_config;
    const newLineItem = createEmptyLineItem(templateConfig, currentOrder.details.length + 1);

    setFormData((prev) => {
      const newOrders = [...prev.orders];
      newOrders[orderIndex] = {
        ...newOrders[orderIndex],
        details: [...newOrders[orderIndex].details, newLineItem],
      };
      return { ...prev, orders: newOrders };
    });
  };

  const handleRemoveLineItem = (orderIndex: number, lineItemIndex: number) => {
    setFormData((prev) => {
      const newOrders = [...prev.orders];
      newOrders[orderIndex] = {
        ...newOrders[orderIndex],
        details: newOrders[orderIndex].details.filter((_, i) => i !== lineItemIndex),
      };
      return { ...prev, orders: newOrders };
    });
  };

  const handleLineItemChange = (
    orderIndex: number,
    lineItemIndex: number,
    field: string,
    value: any
  ) => {
    setFormData((prev) => {
      const newOrders = [...prev.orders];
      newOrders[orderIndex] = {
        ...newOrders[orderIndex],
        details: newOrders[orderIndex].details.map((detail, i) =>
          i === lineItemIndex ? { ...detail, [field]: value } : detail
        ),
      };
      return { ...prev, orders: newOrders };
    });

    const errorKey = `line_${orderIndex}_${lineItemIndex}_${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const handleValidate = useCallback((dataToValidate?: ASNFormData) => {
    const data = dataToValidate || formData;
    // WDEV-53: pass the TP template so requiredNameTypes / namesByType rules
    // run. Previous call ignored the template, which let missing Ship To
    // rows through as "all fields valid" for Cat Antares.
    const validationErrors = validateASN(data, tradingPartner?.template?.template_config);
    const errorRecord = getErrorsAsRecord(validationErrors);
    setErrors(errorRecord);
    setShowValidation(true);

    const isValid = validationErrors.length === 0;

    eventBus.emit('document:validated', {
      type: 'asn',
      documentId,
      isValid,
      errors: errorRecord,
    });

    return isValid;
  }, [formData, documentId, tradingPartner]);

  const handleExportJSON = useCallback(() => {
    const isValid = handleValidate();
    if (!isValid) {
      return;
    }
    downloadJSON(formData);

    eventBus.emit('document:exported', {
      type: 'asn',
      documentId,
      data: formData,
      format: 'json',
    });
  }, [formData, documentId, handleValidate]);

  const handlePreviewJSON = () => {
    const json = exportToJSON(formData);
    setJsonPreview(json);
    setShowJsonPreview(true);
  };

  const handleSave = useCallback(async () => {
    if (isSaveBlockedByExpiry()) {
      setSaveErrorDetail(EXPIRED_SAVE_MSG);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 5000);
      return;
    }
    setSaveErrorDetail(null);
    let dataToSave = { ...formData };

    if (!dataToSave.shipmentIdentification || dataToSave.shipmentIdentification.trim() === '') {
      const newId = generateReferenceNumber('ASN');
      dataToSave = { ...dataToSave, shipmentIdentification: newId };
      setFormData(dataToSave);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.shipmentIdentification;
        return newErrors;
      });
    }

    setSaveStatus('saving');
    try {
      const isValid = handleValidate(dataToSave);
      const status = isValid ? 'valid' : 'draft';
      dataToSave = { ...dataToSave, status };
      setFormData(dataToSave);

      if (!documentId) {
        console.error('Cannot save: no documentId. Document should have been created on partner selection.');
        setSaveStatus('error');
        setSaveErrorDetail(null);
        setTimeout(() => setSaveStatus('idle'), 3000);
        return;
      }

      await updateDocument(documentId, dataToSave, status, tradingPartner?.name);
      eventBus.emit('document:status-updated', {
        type: 'asn',
        documentId,
        status,
      });

      setSavedFormSnapshot(JSON.stringify(dataToSave));
      setHasUnsavedChanges(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save document:', error);
      setSaveStatus('error');
      setSaveErrorDetail(null);
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [documentId, formData, handleValidate, tradingPartner]);

  const handleSaveAndExit = useCallback(async () => {
    await handleSave();
    setTimeout(() => onBack(), 500);
  }, [handleSave, onBack]);

  const handleBackClick = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowExitConfirm(true);
    } else {
      onBack();
    }
  }, [hasUnsavedChanges, onBack]);

  const handleExitWithoutSaving = useCallback(() => {
    setShowExitConfirm(false);
    onBack();
  }, [onBack]);

  const handleSaveAndExitConfirm = useCallback(async () => {
    setShowExitConfirm(false);
    await handleSave();
    setTimeout(() => onBack(), 500);
  }, [handleSave, onBack]);

  useKeyboardShortcut('s', handleSave);
  useKeyboardShortcut('d', handleValidate);
  useKeyboardShortcut('e', handleExportJSON);
  useKeyboardShortcut('o', handleAddOrder, [handleAddOrder]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShowShortcuts(true);
      }
      if (e.key === 'Escape') {
        setShowJsonPreview(false);
        setShowShortcuts(false);
        setShowExitConfirm(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if ((window as any).Cypress) {
        return;
      }

      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const validationErrorCount = Object.keys(errors).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading document...</div>
      </div>
    );
  }

  // Validate Trading Partner is present
  if (!tradingPartner && !documentId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="text-red-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Missing Trading Partner</h3>
            </div>
          </div>
          <p className="text-gray-600 mb-6">
            A Trading Partner is required to create a document. Please go back and select a Trading Partner first.
          </p>
          <button
            onClick={onBack}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Documents
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto px-4 py-6">
        <div className="mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            <button onClick={handleBackClick} className="hover:text-gray-900 flex items-center gap-1">
              <ArrowLeft size={16} />
              Back to Documents
            </button>
            <span>/</span>
            <span>
              {documentId
                ? isCopy
                  ? 'Edit ASN (Copy)'
                  : 'Edit ASN'
                : 'New ASN'}
            </span>
            {hasUnsavedChanges && (
              <span className="text-orange-600 text-xs font-medium">• Unsaved Changes</span>
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package size={32} />
            {documentId
              ? isCopy
                ? `Edit ASN (Copy): ${formData.shipmentIdentification}`
                : `Edit ASN: ${formData.shipmentIdentification}`
              : 'Create New ASN'}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Press <kbd className="kbd">?</kbd> for shortcuts
          </p>
        </div>

        {showValidation && validationErrorCount > 0 && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
            <div>
              <h3 className="font-medium text-red-900 text-sm">Validation Errors ({validationErrorCount})</h3>
              <ul className="text-xs text-red-700 mt-1 list-disc list-inside space-y-0.5">
                {Object.entries(errors).map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {showValidation && validationErrorCount === 0 && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3 flex items-start gap-2">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
            <div>
              <h3 className="font-medium text-green-900 text-sm">Ready to Export</h3>
              <p className="text-xs text-green-700 mt-0.5">All fields valid</p>
            </div>
          </div>
        )}

        {saveStatus === 'error' && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
            <div>
              <h3 className="font-medium text-red-900 text-sm">Save Failed</h3>
              <p className="text-xs text-red-700 mt-0.5">{saveErrorDetail ?? 'Failed to save document. Please try again.'}</p>
            </div>
          </div>
        )}

        <div className="flex gap-2 mb-4 flex-wrap">
          <button
            onClick={() => handleValidate()}
            className="btn-primary px-3 py-1.5 text-sm"
            title="Ctrl+D"
          >
            <CheckCircle size={16} className="inline mr-1.5" />
            Validate
          </button>
          <button
            onClick={handleSave}
            className="btn-primary px-3 py-1.5 text-sm"
            title="Ctrl+S"
            disabled={saveStatus === 'saving' || sessionExpired}
          >
            <Save size={16} className="inline mr-1.5" />
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved!' : 'Save'}
          </button>
          <button
            onClick={handleSaveAndExit}
            className="btn-secondary px-3 py-1.5 text-sm"
            disabled={saveStatus === 'saving' || sessionExpired}
          >
            <Save size={16} className="inline mr-1.5" />
            Save & Exit
          </button>
          
          
          <button
            onClick={() => setShowShortcuts(true)}
            className="btn-secondary px-3 py-1.5 text-sm ml-auto"
            title="?"
          >
            <Keyboard size={16} className="inline mr-1.5" />
            Shortcuts
          </button>
        </div>

        <div className="card-compact section-compact mb-4">
          <HeaderSection data={formData} onChange={handleHeaderChange} errors={errors} />
        </div>

        <div className="card-compact section-compact">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Orders</h2>
            <button
              onClick={handleAddOrder}
              className="btn-primary px-3 py-1.5 text-sm"
              title="Ctrl+O"
            >
              <Plus size={16} className="inline mr-1.5" />
              Add Order
            </button>
          </div>

          <div className="space-y-3">
            {formData.orders.map((order, orderIndex) => (
              <OrderSection
                key={orderIndex}
                order={order}
                orderIndex={orderIndex}
                onChange={handleOrderChange}
                onRemove={handleRemoveOrder}
                onAddLineItem={handleAddLineItem}
                onRemoveLineItem={handleRemoveLineItem}
                onLineItemChange={handleLineItemChange}
                errors={errors}
              />
            ))}

            {formData.orders.length === 0 && (
              <div className="text-center py-8 bg-gray-50 rounded-md border-2 border-dashed border-gray-300">
                <p className="text-sm text-gray-500 mb-2">No orders yet</p>
                <button
                  onClick={handleAddOrder}
                  className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                >
                  Add your first order
                </button>
              </div>
            )}
          </div>
        </div>

        {showJsonPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h3 className="text-base font-semibold">JSON Preview</h3>
                <button
                  onClick={() => setShowJsonPreview(false)}
                  className="text-gray-500 hover:text-gray-700 font-bold text-xl"
                >
                  ×
                </button>
              </div>
              <div className="p-4 overflow-auto flex-1">
                <pre className="text-xs bg-gray-50 p-3 rounded border border-gray-200 overflow-auto">
                  {jsonPreview}
                </pre>
              </div>
              <div className="px-4 py-3 border-t flex gap-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(jsonPreview);
                  }}
                  className="btn-primary px-3 py-1.5 text-sm"
                >
                  Copy
                </button>
                <button
                  onClick={() => setShowJsonPreview(false)}
                  className="btn-secondary px-3 py-1.5 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showExitConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <AlertCircle className="text-orange-600" size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Unsaved Changes
                    </h3>
                    <p className="text-sm text-gray-600">
                      You have unsaved changes that will be lost if you continue. What would you like to do?
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button
                  onClick={() => setShowExitConfirm(false)}
                  className="btn-secondary px-4 py-2 text-sm order-3 sm:order-1"
                >
                  Stay on Page
                </button>
                <button
                  onClick={handleExitWithoutSaving}
                  className="btn-secondary px-4 py-2 text-sm order-2 sm:order-2 text-red-600 hover:bg-red-50"
                >
                  Exit Without Saving
                </button>
                <button
                  onClick={handleSaveAndExitConfirm}
                  className="btn-primary px-4 py-2 text-sm order-1 sm:order-3"
                  disabled={sessionExpired}
                >
                  Save and Exit
                </button>
              </div>
            </div>
          </div>
        )}

        <KeyboardShortcuts isOpen={showShortcuts} onClose={() => setShowShortcuts(false)} />
      </div>
    </div>
  );
}
